import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Building2, Search, CheckCircle, XCircle, AlertTriangle, FileText,
  MapPin, Phone, Briefcase, TrendingUp, Copy, Clock, ChevronRight,
  Globe, Mail, Shield, Hash, Calendar, Users, Banknote, ExternalLink,
  Info, Loader2, ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DataField = ({ label, value, mono, highlight, copyable, onCopy }) => {
  if (!value || value === "N/A") return (
    <div>
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-sm text-slate-300 italic">-</p>
    </div>
  );
  return (
    <div className="group">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="flex items-center gap-1.5">
        <p className={`text-sm ${mono ? "font-mono" : ""} ${highlight ? "bg-slate-800 text-white px-2 py-0.5 rounded font-medium" : "text-slate-200"}`}>
          {value}
        </p>
        {copyable && (
          <button onClick={() => onCopy(String(value))} className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-700" data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <Copy className="w-3 h-3 text-slate-400" />
          </button>
        )}
      </div>
    </div>
  );
};

const HealthScore = ({ data }) => {
  if (!data?.dati_finanziari) return null;
  const fin = data.dati_finanziari;
  let score = 50;
  let factors = [];
  if (data.stato_attivita === "ATTIVA") { score += 20; factors.push({ label: "Attiva", positive: true }); }
  else if (data.stato_attivita === "CESSATA") { score -= 40; factors.push({ label: "Cessata", positive: false }); }
  if (fin.utile_perdita > 0) { score += 15; factors.push({ label: "Utile positivo", positive: true }); }
  else if (fin.utile_perdita < 0) { score -= 15; factors.push({ label: "In perdita", positive: false }); }
  if (fin.patrimonio_netto > 0) { score += 10; factors.push({ label: "Patrimonio positivo", positive: true }); }
  if (fin.numero_dipendenti > 0) { score += 5; factors.push({ label: `${fin.numero_dipendenti} dipendenti`, positive: true }); }
  score = Math.max(0, Math.min(100, score));
  const color = score >= 70 ? "text-emerald-400" : score >= 40 ? "text-amber-400" : "text-red-400";
  const bg = score >= 70 ? "bg-emerald-500/10 border-emerald-500/20" : score >= 40 ? "bg-amber-500/10 border-amber-500/20" : "bg-red-500/10 border-red-500/20";

  return (
    <div className={`rounded-xl border p-4 ${bg}`} data-testid="health-score">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Indice Affidabilità</h4>
        <span className={`text-2xl font-black ${color}`}>{score}/100</span>
      </div>
      <div className="w-full bg-slate-800 rounded-full h-2 mb-3">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={`h-2 rounded-full ${score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-red-500"}`}
        />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {factors.map((f, i) => (
          <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full border ${f.positive ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/5" : "border-red-500/30 text-red-400 bg-red-500/5"}`}>
            {f.positive ? "+" : "-"} {f.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const FinancialCard = ({ label, value, icon: Icon, positive }) => {
  const formatted = value != null ? `€${Number(value).toLocaleString('it-IT')}` : "-";
  const isNeg = value != null && value < 0;
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 text-center">
      <Icon className={`w-4 h-4 mx-auto mb-1.5 ${isNeg ? "text-red-400" : "text-emerald-400"}`} />
      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-base font-bold ${isNeg ? "text-red-400" : value != null ? "text-white" : "text-slate-500"}`}>{formatted}</p>
    </div>
  );
};

export default function CompanyVerification() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/piva-history`, { credentials: "include" });
      if (res.ok) setHistory(await res.json());
    } catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiato negli appunti");
  };

  const copyAllData = () => {
    if (!result?.data) return;
    const d = result.data;
    const lines = [
      `Denominazione: ${d.denominazione}`,
      `P.IVA: ${d.partita_iva}`,
      `CF: ${d.codice_fiscale}`,
      `Stato: ${d.stato_attivita}`,
      `Indirizzo: ${d.indirizzo}`,
      `PEC: ${d.pec}`,
      `SDI: ${d.codice_sdi}`,
      `ATECO: ${d.codice_ateco} - ${d.descrizione_ateco}`,
    ];
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Tutti i dati copiati");
  };

  const handleSearch = async (piva) => {
    const searchPiva = piva || query;
    if (!searchPiva || searchPiva.length !== 11) {
      toast.error("Inserisci una Partita IVA valida di 11 cifre");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await fetch(`${API}/admin/verify-piva`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ piva: searchPiva })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Errore verifica");
      setResult(data);
      if (piva) setQuery(piva);

      // Save to history
      if (data.data) {
        await fetch(`${API}/admin/piva-history`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            piva: searchPiva,
            denominazione: data.data.denominazione || "",
            stato: data.data.stato_attivita || "",
            valid: data.valid
          })
        });
        fetchHistory();
      }

      if (data.valid && !data.warning) toast.success("Azienda verificata!");
      else if (data.warning) toast.warning(data.message);
      else toast.error(data.message);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const d = result?.data;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="company-verification">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <span className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white p-2.5 rounded-xl">
          <Building2 className="w-5 h-5" />
        </span>
        <div>
          <h2 className="text-xl font-bold tracking-tight">Verifica Azienda</h2>
          <p className="text-sm text-muted-foreground">Dati camerali completi da OpenAPI / Cerved</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Left Panel: Search + History */}
        <div className="lg:col-span-1 space-y-4">
          {/* Search */}
          <Card className="bg-slate-950 border-slate-800">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Search className="w-4 h-4 text-indigo-400" />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Cerca P.IVA</span>
              </div>
              <Input
                placeholder="11 cifre..."
                value={query}
                onChange={(e) => setQuery(e.target.value.replace(/[^0-9]/g, '').slice(0, 11))}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="font-mono text-lg tracking-widest bg-slate-900 border-slate-700 text-white h-12"
                data-testid="piva-input"
              />
              <Button
                onClick={() => handleSearch()}
                disabled={loading || query.length !== 11}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="piva-search-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifica...</> : <><Search className="w-4 h-4 mr-2" />Verifica P.IVA</>}
              </Button>
              <p className="text-[10px] text-slate-500">Es: 00811720580 (ENEL)</p>
            </CardContent>
          </Card>

          {/* Search History */}
          <Card className="bg-slate-950 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Ricerche Recenti</span>
              </div>
              {historyLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
              ) : history.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-4">Nessuna ricerca</p>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="piva-history">
                  {history.map((h, i) => (
                    <button
                      key={i}
                      onClick={() => handleSearch(h.piva)}
                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-slate-800/80 transition-colors text-left group"
                      data-testid={`history-item-${i}`}
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${h.valid ? "bg-emerald-500" : "bg-red-500"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-mono text-slate-300 truncate">{h.piva}</p>
                        <p className="text-[10px] text-slate-500 truncate">{h.denominazione || "—"}</p>
                      </div>
                      <ChevronRight className="w-3 h-3 text-slate-600 group-hover:text-slate-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel: Results */}
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Card className="bg-slate-950 border-slate-800 border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-20 text-center">
                    <Building2 className="w-16 h-16 text-slate-700 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-400">In attesa di verifica</h3>
                    <p className="text-sm text-slate-500 mt-1 max-w-md">
                      Inserisci una Partita IVA per visualizzare dati identificativi, sede legale, contatti, classificazione ATECO e dati finanziari.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div key="results" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
                {/* Status Banner */}
                <Card className="bg-slate-950 border-slate-800 overflow-hidden" data-testid="piva-result">
                  <div className={`h-1 ${d?.stato_attivita === "ATTIVA" ? "bg-emerald-500" : d?.stato_attivita === "CESSATA" ? "bg-red-500" : "bg-amber-500"}`} />
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-white tracking-tight" data-testid="company-name">{d?.denominazione || "N/A"}</h3>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-sm font-mono text-slate-400">P.IVA {d?.partita_iva}</span>
                          {d?.codice_fiscale && d.codice_fiscale !== d?.partita_iva && (
                            <span className="text-sm font-mono text-slate-400">CF {d.codice_fiscale}</span>
                          )}
                        </div>
                        {result.message && (
                          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                            <Info className="w-3 h-3" /> {result.message}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={copyAllData} className="text-slate-400 hover:text-white h-8 px-2" data-testid="copy-all-btn">
                          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copia tutto
                        </Button>
                        {d?.stato_attivita === "ATTIVA" ? (
                          <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 text-emerald-400 px-3 py-1.5 rounded-full border border-emerald-500/20 text-xs font-bold" data-testid="status-badge">
                            <CheckCircle className="w-3.5 h-3.5" /> ATTIVA
                          </span>
                        ) : d?.stato_attivita === "CESSATA" ? (
                          <span className="inline-flex items-center gap-1.5 bg-red-500/10 text-red-400 px-3 py-1.5 rounded-full border border-red-500/20 text-xs font-bold" data-testid="status-badge">
                            <XCircle className="w-3.5 h-3.5" /> CESSATA
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 bg-amber-500/10 text-amber-400 px-3 py-1.5 rounded-full border border-amber-500/20 text-xs font-bold" data-testid="status-badge">
                            <AlertTriangle className="w-3.5 h-3.5" /> {d?.stato_attivita}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {result.valid || result.warning ? (
                  <>
                    {/* Health Score + Identification Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Health Score */}
                      <HealthScore data={d} />

                      {/* Dati Identificativi */}
                      <Card className="bg-slate-950 border-slate-800 md:col-span-2">
                        <CardContent className="p-4">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-2 mb-3">
                            <FileText className="w-3.5 h-3.5" /> Dati Identificativi
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
                            <DataField label="Codice Fiscale" value={d?.codice_fiscale} mono copyable onCopy={copyToClipboard} />
                            <DataField label="Numero REA" value={d?.numero_rea} mono copyable onCopy={copyToClipboard} />
                            <DataField label="Codice SDI" value={d?.codice_sdi} mono highlight copyable onCopy={copyToClipboard} />
                            <DataField label="Forma Giuridica" value={d?.forma_giuridica} />
                            <DataField label="Data Registrazione" value={d?.data_registrazione} />
                            <DataField label="Stato Attività" value={d?.stato_attivita} highlight />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Sede + Contatti + ATECO Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Sede Legale */}
                      <Card className="bg-slate-950 border-slate-800">
                        <CardContent className="p-4">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-2 mb-3">
                            <MapPin className="w-3.5 h-3.5" /> Sede Legale
                          </h4>
                          <div className="space-y-3">
                            <DataField label="Indirizzo" value={d?.indirizzo} copyable onCopy={copyToClipboard} />
                            <div className="grid grid-cols-2 gap-3">
                              <DataField label="CAP" value={d?.cap} mono />
                              <DataField label="Comune" value={d?.comune} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <DataField label="Provincia" value={d?.provincia} />
                              <DataField label="Regione" value={d?.regione} />
                            </div>
                            {d?.coordinate_gps?.latitudine && (
                              <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">GPS</p>
                                <a
                                  href={`https://www.google.com/maps?q=${d.coordinate_gps.latitudine},${d.coordinate_gps.longitudine}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs font-mono text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                                  data-testid="gps-link"
                                >
                                  {d.coordinate_gps.latitudine.toFixed(4)}, {d.coordinate_gps.longitudine.toFixed(4)}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Contatti */}
                      <Card className="bg-slate-950 border-slate-800">
                        <CardContent className="p-4">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-purple-400 flex items-center gap-2 mb-3">
                            <Phone className="w-3.5 h-3.5" /> Contatti
                          </h4>
                          <div className="space-y-3">
                            <DataField label="Telefono" value={d?.telefono} copyable onCopy={copyToClipboard} />
                            <DataField label="Fax" value={d?.fax} />
                            <DataField label="Email" value={d?.email} mono copyable onCopy={copyToClipboard} />
                            <div>
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">PEC</p>
                              <p className="text-sm font-mono text-purple-300 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20 truncate">
                                {d?.pec || "-"}
                              </p>
                            </div>
                            {d?.sito_web && d.sito_web !== "N/A" && (
                              <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Sito Web</p>
                                <a href={d.sito_web.startsWith("http") ? d.sito_web : `https://${d.sito_web}`} target="_blank" rel="noopener noreferrer"
                                  className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                                  <Globe className="w-3 h-3" /> {d.sito_web}
                                </a>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {/* ATECO */}
                      <Card className="bg-slate-950 border-slate-800">
                        <CardContent className="p-4">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400 flex items-center gap-2 mb-3">
                            <Briefcase className="w-3.5 h-3.5" /> Classificazione ATECO
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Codice</p>
                              <span className="font-mono text-lg font-black text-amber-400 bg-amber-500/10 px-3 py-1 rounded border border-amber-500/20">
                                {d?.codice_ateco || "-"}
                              </span>
                            </div>
                            <DataField label="Descrizione Attività" value={d?.descrizione_ateco} />
                            <DataField label="Sezione" value={d?.sezione_ateco} />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Dati Finanziari */}
                    {d?.dati_finanziari && (
                      <Card className="bg-slate-950 border-slate-800" data-testid="financial-data">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-2">
                              <TrendingUp className="w-3.5 h-3.5" /> Dati Finanziari
                            </h4>
                            {d.dati_finanziari.anno_riferimento && d.dati_finanziari.anno_riferimento !== "N/A" && (
                              <span className="text-[10px] bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full border border-slate-700">
                                Anno {d.dati_finanziari.anno_riferimento}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <FinancialCard label="Capitale Sociale" value={d.dati_finanziari.capitale_sociale} icon={Shield} />
                            <FinancialCard label="Ricavi Operativi" value={d.dati_finanziari.ricavi_operativi} icon={Banknote} />
                            <FinancialCard label="Utile/Perdita" value={d.dati_finanziari.utile_perdita} icon={TrendingUp} />
                            <FinancialCard label="Patrimonio Netto" value={d.dati_finanziari.patrimonio_netto} icon={Banknote} />
                            <FinancialCard label="Dipendenti" value={d.dati_finanziari.numero_dipendenti} icon={Users} />
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                ) : (
                  /* Invalid/Ceased Company */
                  <Card className="bg-slate-950 border-red-500/30">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-red-500/10 rounded-xl">
                          <XCircle className="w-8 h-8 text-red-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-red-400">Blocco Operativo</h4>
                          <p className="mt-1 text-slate-300">{result.message}</p>
                          {d && (
                            <div className="mt-3 p-3 bg-slate-900 rounded-lg border border-slate-800">
                              <p className="text-sm font-mono text-slate-400">Ragione Sociale: {d.denominazione}</p>
                              <p className="text-sm font-mono text-slate-400">Stato: {d.stato_attivita}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Warning banner */}
                {result.warning && (
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-300">{result.message}</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
